import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { ShoppingCart, Link, Bot, Lock, Mail, Globe, CheckCircle, Star, Zap, Lightbulb, Package, Truck, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import { organizationSchema, websiteSchema } from '../data/structuredData';

const TestimonialCarousel = ({ testimonials }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const cardsPerPage = 3;
  const totalPages = Math.ceil(testimonials.length / cardsPerPage);

  const goToPage = useCallback((page) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentPage(page);
    setTimeout(() => setIsTransitioning(false), 500);
  }, [isTransitioning]);

  const nextPage = useCallback(() => {
    goToPage((currentPage + 1) % totalPages);
  }, [currentPage, totalPages, goToPage]);

  const prevPage = useCallback(() => {
    goToPage((currentPage - 1 + totalPages) % totalPages);
  }, [currentPage, totalPages, goToPage]);

  // Auto-rotation
  useEffect(() => {
    if (isPaused || totalPages <= 1) return;
    const timer = setInterval(nextPage, 6000);
    return () => clearInterval(timer);
  }, [isPaused, nextPage, totalPages]);

  // Get current visible testimonials
  const startIndex = currentPage * cardsPerPage;
  const visibleTestimonials = testimonials.slice(startIndex, startIndex + cardsPerPage);

  // Pad to always show 3 columns visually (prevents layout shift)
  while (visibleTestimonials.length < cardsPerPage && testimonials.length >= cardsPerPage) {
    // Wrap around from the beginning
    const wrapIndex = visibleTestimonials.length - (testimonials.length - startIndex);
    if (wrapIndex >= 0 && wrapIndex < testimonials.length) {
      visibleTestimonials.push(testimonials[wrapIndex]);
    } else {
      break;
    }
  }

  return (
    <div
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Cards */}
      <div className="relative overflow-hidden">
        <div
          className={`grid md:grid-cols-3 gap-8 transition-all duration-500 ease-in-out ${
            isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
          }`}
        >
          {visibleTestimonials.map((testimonial, index) => (
            <div
              key={`${currentPage}-${index}`}
              className="bg-white/10 backdrop-blur-md p-8 hover:bg-white/20 transition-all duration-300"
            >
              <div className="flex mb-6">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 mr-1 fill-white" />
                ))}
              </div>
              <p className="text-white mb-6 text-lg leading-relaxed italic">
                {`"${testimonial.text}"`}
              </p>
              <div className="border-t border-white/20 pt-6">
                <p className="font-bold text-white text-lg">{testimonial.name}</p>
                <p className="text-gray-300">{testimonial.company}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-6 mt-12">
          {/* Prev arrow */}
          <button
            onClick={prevPage}
            className="w-10 h-10 flex items-center justify-center border border-white/20 hover:border-white/60 hover:bg-white/10 transition-all duration-300 text-white/60 hover:text-white"
            aria-label="Previous testimonials"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          {/* Dot indicators */}
          <div className="flex gap-2">
            {Array.from({ length: totalPages }).map((_, index) => (
              <button
                key={index}
                onClick={() => goToPage(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentPage
                    ? 'w-8 bg-white'
                    : 'w-2 bg-white/30 hover:bg-white/60'
                }`}
                aria-label={`Go to page ${index + 1}`}
              />
            ))}
          </div>

          {/* Next arrow */}
          <button
            onClick={nextPage}
            className="w-10 h-10 flex items-center justify-center border border-white/20 hover:border-white/60 hover:bg-white/10 transition-all duration-300 text-white/60 hover:text-white"
            aria-label="Next testimonials"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
};

const HomePage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Handle hash navigation
  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);

  const testimonials = [
    { name: "Jeff R.", company: "Sr Manager, UPS", rating: 5, text: "Business acumen, technical insights, project planning expertise, and leadership prowess which are rarely found in combination." },
    { name: "Krishna B.", company: "Sr Manager, Amazon", rating: 5, text: "Thank you for being comprehensive and raising the bar." },
    { name: "Girish K.", company: "Founder, FreightLens", rating: 5, text: "Broad range of skills but a truly exceptional ability to design technical solutions." },
 //   { name: "Stephen C.", company: "CTO, TripShift", rating: 5, text: "WIP" },
  ];

  return (
    <div className="min-h-screen text-black">
      {/* Background image */}
      <div
        fetchPriority="high"
        className="fixed inset-0 pointer-events-none z-10 bg-top bg-scroll md:bg-fixed bg-no-repeat mobile-bg"
        style={{
          backgroundImage: 'url("/container.jpg")',
          backgroundSize: 'cover'
        }}
      />
      
      {/* Background overlay for readability */}
      <div className="fixed inset-0 bg-white/80 pointer-events-none z-20"></div>
<Seo path="/" jsonLd={[organizationSchema, websiteSchema]} />
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative z-30">
        <div className="container mx-auto">
          <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="max-w-4xl">
              <p className="text-lg md:text-xl font-bold uppercase tracking-widest text-gray-500 mb-4">
                Delivering Technology
              </p>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-8 leading-tight">
                Integration &amp; AI Automation
                <br />
                <span className="relative inline-block">
                  for Logistics &amp; eCommerce
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-600 mb-12 max-w-3xl leading-relaxed">
                We built these systems at Amazon, Flexport, and UPS — and got paged when
                they broke. Now we build them for companies that need them working in
                weeks, not quarters.
              </p>
              <button
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-black hover:bg-gray-800 text-white px-10 py-5 font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl group inline-flex items-center"
              >
                Bring Us A Problem
                <ArrowRight className="ml-3 w-5 h-5 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications Bar */}
      <section className="py-10 px-6 bg-white border-y border-gray-200 relative z-30">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-gray-400">
              Certified & Proven
            </span>
            <div className="flex items-center gap-8">
              <img 
                src="/AWSAI.png" 
                alt="AWS Certified AI Practitioner" 
                className="h-16 md:h-20 opacity-80 hover:opacity-100 transition-opacity duration-300"
              />
              <img 
                src="/Boomicert.jpeg" 
                alt="Boomi Development & Application Architecture" 
                className="h-16 md:h-20 opacity-80 hover:opacity-100 transition-opacity duration-300"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-20 px-6 bg-gray-50 relative z-30">
        <div className="container mx-auto">
          <h2 className="text-5xl font-black mb-20 text-center">
            Why Tare Labs?
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 shadow-sm hover:shadow-lg transition-all duration-300 group">
              <div className="w-12 h-12 bg-black text-white flex items-center justify-center mb-6 transition-transform group-hover:scale-110">
                <CheckCircle className="w-6 h-6" />
              </div>
              <h3 className="text-2xl font-bold text-black mb-4">Built at Amazon, Flexport & UPS</h3>
              <p className="text-gray-600 leading-relaxed">13+ years designing and building the integration systems that power global logistics. We've architected solutions handling billions of transactions a year.</p>
            </div>
            <div className="bg-white p-8 shadow-sm hover:shadow-lg transition-all duration-300 group">
              <div className="w-12 h-12 bg-black text-white flex items-center justify-center mb-6 transition-transform group-hover:scale-110">
                <Link className="w-6 h-6" />
              </div>
              <h3 className="text-2xl font-bold text-black mb-4">We Speak EDI & API</h3>
              <p className="text-gray-600 leading-relaxed">Deep expertise in best in class iPaaS solutions and the platforms your enterprise partners already run on. No ramp-up time, just results.</p>
            </div>
            
            <div className="bg-white p-8 shadow-sm hover:shadow-lg transition-all duration-300 group">
              <div className="w-12 h-12 bg-black text-white flex items-center justify-center mb-6 transition-transform group-hover:scale-110">
                <Bot className="w-6 h-6" />
              </div>
              <h3 className="text-2xl font-bold text-black mb-4">AI-Driven Automation</h3>
              <p className="text-gray-600 leading-relaxed">We identify the manual processes costing you time and money, then build intelligent automation that scales with your business instead of your headcount.</p>
            </div>
            
            <div className="bg-white p-8 shadow-sm hover:shadow-lg transition-all duration-300 group">
              <div className="w-12 h-12 bg-black text-white flex items-center justify-center mb-6 transition-transform group-hover:scale-110">
                <Zap className="w-6 h-6" />
              </div>
              <h3 className="text-2xl font-bold text-black mb-4">Hands-On, Not Slide Decks</h3>
              <p className="text-gray-600 leading-relaxed">We don't just advise: we build, implement, and deliver. Real-world execution from those who've shipped at scale.</p>
            </div>
            
            <div className="bg-white p-8 shadow-sm hover:shadow-lg transition-all duration-300 group">
              <div className="w-12 h-12 bg-black text-white flex items-center justify-center mb-6 transition-transform group-hover:scale-110">
                <Lightbulb className="w-6 h-6" />
              </div>
              <h3 className="text-2xl font-bold text-black mb-4">Right-Sized Architecture</h3>
              <p className="text-gray-600 leading-relaxed">Whether you're a growing startup or an established mid-market company, we help you avoid costly platform mistakes we've seen at Fortune 500s. Invest only in what drives efficiency, revenue, and growth.</p>
            </div>
            
            <div className="bg-white p-8 shadow-sm hover:shadow-lg transition-all duration-300 group">
              <div className="w-12 h-12 bg-black text-white flex items-center justify-center mb-6 transition-transform group-hover:scale-110">
                <Lock className="w-6 h-6" />
              </div>
              <h3 className="text-2xl font-bold text-black mb-4">Security-First Compliance</h3>
              <p className="text-gray-600 leading-relaxed">Enterprise-grade security and compliance baked into every solution. Reduce risk from day one, not as an afterthought.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Serve Section */}
      <section className="py-20 px-6 relative z-30">
        <div className="container mx-auto">

          {/* Industry Focus */}
          <div className="bg-gray-50 p-10 shadow-sm">
            <h3 className="text-3xl font-black text-center mb-12">
              Customer Industries
            </h3>
            
            <div className="grid md:grid-cols-3 lg:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-black text-white flex items-center justify-center mx-auto mb-4">
                  <ShoppingCart className="w-8 h-8" />
                </div>
                <h4 className="font-bold text-lg mb-2">eCommerce</h4>
                <p className="text-sm text-gray-600">D2C and B2B Marketplaces, Omnichannel Retail</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-black text-white flex items-center justify-center mx-auto mb-4">
                  <Package className="w-8 h-8" />
                </div>
                <h4 className="font-bold text-lg mb-2">3PL & 4PL</h4>
                <p className="text-sm text-gray-600">Freight Forwarding, Order Management, Customs Brokerage, Control Tower</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-black text-white flex items-center justify-center mx-auto mb-4">
                  <Truck className="w-8 h-8" />
                </div>
                <h4 className="font-bold text-lg mb-2">Trucking</h4>
                <p className="text-sm text-gray-600">FTL, LTL, Final Mile, Drayage, Cartage, Parcel</p>
              </div>
            
            </div>
          </div>

{/*         
          <div className="mt-16 text-center">
            <div className="inline-block bg-yellow-50 border-2 border-yellow-400 p-8 max-w-3xl">
              <h3 className="text-2xl font-bold mb-4">
                You're Our Ideal Partner If...
              </h3>
              <div className="grid md:grid-cols-2 gap-4 text-left">
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">You need systems that integrate and scale</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Your team spends too much time on manual processes</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">You need enterprise features without enterprise cost</span>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">You value expertise and are ready to invest in efficiency</span>
                </div>
              </div>
              <button 
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="mt-8 bg-black hover:bg-gray-800 text-white px-8 py-4 font-bold transition-all duration-300 transform hover:scale-105"
              >
                Let's discuss your pain points
              </button>
            </div>
          </div>
*/}
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 px-4 bg-white relative z-30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-black mb-4">How We Work</h2>
          <p className="text-xl text-gray-600 mb-16 max-w-6xl mx-auto">
            We don't sell you tools, and we don't stop at a recommendation. We find
            what's actually costing you money, build the fix, and stay on it after it ships.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                title: "Find the Friction",
                desc: "We follow the work end-to-end with your team: where the bottlenecks are, what's eating hours, what breaks at 2am. You get a documented map of the current process and a number on what it's costing you.",
              },
              {
                step: "02",
                title: "Solution Design",
                desc: "We architect the right mix of agentic AI, workflow automation, and integration — scoped to your systems, your budget, and the team you actually have. No platform you don't need.",
              },
              {
                step: "03",
                title: "Build & Deploy",
                desc: "We write it, test it, and ship it. Error handling, monitoring, and runbooks included — not a proof of concept you have to productionize yourself.",
              },
              {
                step: "04",
                title: "Stay on the Hook",
                desc: "We don't disappear at go-live. We watch it run, fix what breaks, and tune it as your volume and trading partners change. You own the system; we make sure it keeps earning.",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="group border-t-4 border-black pt-6 hover:bg-gray-50 p-6 transition-all duration-300"
              >
                <div className="text-sm font-mono text-gray-400 mb-2">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-6 bg-black text-white relative z-30">
        <div className="container mx-auto">
          <h2 className="text-5xl font-black mb-20 text-center">
            Customer Success Stories
          </h2>
          
          <TestimonialCarousel testimonials={testimonials} />
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-6 bg-gray-50 relative z-30">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-black mb-8">
              Ready to Optimize?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Let's discuss how we can streamline your business and accelerate your growth with technology.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 mb-16">
              <a 
                href="mailto:dan@tarelabs.com"
                className="bg-white p-8 text-center shadow-sm hover:shadow-lg transition-all duration-300 transform hover:scale-105 cursor-pointer"
              >
                <div className="w-16 h-16 bg-black text-white flex items-center justify-center mx-auto mb-6">
                  <Mail className="w-8 h-8" />
                </div>
                <h3 className="font-bold text-black mb-3 text-xl">Email</h3>
                <p className="text-gray-600 font-mono">dan@tarelabs.com</p>
              </a>
              <a 
                href="https://calendly.com/dan-tarelabs/new-meeting"
                className="bg-white p-8 text-center shadow-sm hover:shadow-lg transition-all duration-300 transform hover:scale-105 cursor-pointer"
              >
                <div className="w-16 h-16 bg-black text-white flex items-center justify-center mx-auto mb-6">
                  <Globe className="w-8 h-8" />
                </div>
                <h3 className="font-bold text-black mb-3 text-xl">Schedule</h3>
                <p className="text-gray-600">Book a free consultation</p>
              </a>
          </div>
        </div>
      </section>
     <Footer />
    </div>
  );
};

export default HomePage;